import { FaroTextField } from "@components/common/faro-text-field/faro-text-field";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { updateGroupDetails } from "@store/groups/groups-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { MIN_GROUP_NAME_LENGTH } from "@utils/group-utils";
import { ChangeGroupDetailsEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface Props {
  /** Human readable name of the group */
  name: string;

  /** True if the group name should be editable */
  isEditable: boolean;
}

/** Renders and updates the group name */
export function EditableGroupName({ name, isEditable }: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorContext();
  const dispatch = useAppDispatch();

  /**
   * Updates the group name in the backend and returns the new value if successful.
   * Otherwise, returns the old value.
   *
   * @param value The new project name.
   */
  async function onUpdateName(value: string): Promise<string> {
    trackEvent({
      name: ChangeGroupDetailsEvents.changeGroupName,
      props: { newNameLength: value.length },
    });

    try {
      await dispatch(
        updateGroupDetails({
          payload: { name: value.trim() },
        })
      );
      return value;
    } catch (error) {
      handleErrorWithToast({
        id: `changeGroupName-${Date.now().toString()}`,
        title: "Could not change group name",
        error,
      });
      return name ?? "";
    }
  }
  return (
    <FaroTextField
      label="Group name"
      initialValue={name}
      faroVariant="main"
      fontSize="18px"
      isReadOnly={!isEditable}
      shouldShowTooltip
      minInputLength={MIN_GROUP_NAME_LENGTH}
      onConfirmed={onUpdateName}
    />
  );
}
